import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useStoreAmount } from '@sorare/core/src/hooks/useStoreAmount';
import {
  MonetaryAmount,
  monetaryAmountFragment,
} from '@sorare/core/src/lib/monetaryAmount';
import { BasicBenchExtraWrapper } from '@sorare/marketplace/src/components/searchPlayerTable/BasicBenchExtraWrapper';
import { useConversionCreditForPrice } from '@sorare/marketplace/src/hooks/useConversionCreditForPrice';

import type { MarketCardFooterPrice_anyCardInterface } from './__generated__/index.graphql.ts';

const CrossedLabelS = styled(LabelS)`
  text-decoration: line-through;
`;

type Props = {
  anyCard: MarketCardFooterPrice_anyCardInterface;
  applyConversionCredit?: boolean;
};

const RegularPrice = ({
  main,
  exponent,
}: {
  main: string;
  exponent: string | null;
}) => {
  return (
    <>
      <LabelS bold color="var(--c-white)">
        {main}
      </LabelS>
      <LabelXS color="var(--c-nd-600)">{exponent}</LabelXS>
    </>
  );
};

const DiscountedPrice = ({
  anyCard,
  main,
  exponent,
  storeMain,
  storeExponent,
}: {
  anyCard: MarketCardFooterPrice_anyCardInterface;
  main: string;
  exponent: string | null;
  storeMain?: string;
  storeExponent?: string | null;
}) => {
  const { sport } = useSportContext();
  const { livePrimaryOffer } = anyCard || {};

  const { priceDiscountedAmount } = useConversionCreditForPrice({
    price: livePrimaryOffer?.price || {
      ...MonetaryAmount.zero,
      referenceCurrency: SupportedCurrency.USD,
    },
    sport,
  });

  const { main: discountedMain } = useAmountWithConversion({
    monetaryAmount: priceDiscountedAmount,
  });

  if (!storeMain && discountedMain !== main) {
    return (
      <>
        <LabelS bold color="var(--c-onBase-success)">
          {discountedMain}
        </LabelS>
        <CrossedLabelS color="var(--c-nd-600)">{main}</CrossedLabelS>
      </>
    );
  }

  return (
    <RegularPrice
      main={storeMain || main}
      exponent={storeExponent || exponent}
    />
  );
};

export const MarketCardFooterPrice = ({
  anyCard,
  applyConversionCredit = false,
}: Props) => {
  const { livePrimaryOffer } = anyCard || {};
  const { toMonetaryAmount } = useMonetaryAmount();
  const { storeMain, storeExponent } = useStoreAmount(livePrimaryOffer);
  const { main, exponent } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(livePrimaryOffer?.price),
  });
  return (
    <BasicBenchExtraWrapper>
      <Horizontal gap={0.5}>
        {applyConversionCredit ? (
          <DiscountedPrice
            anyCard={anyCard}
            main={main}
            exponent={exponent}
            storeMain={storeMain}
            storeExponent={storeExponent}
          />
        ) : (
          <RegularPrice
            main={storeMain || main}
            exponent={storeExponent || exponent}
          />
        )}
      </Horizontal>
    </BasicBenchExtraWrapper>
  );
};

MarketCardFooterPrice.fragments = {
  anyCardInterface: gql`
    fragment MarketCardFooterPrice_anyCardInterface on AnyCardInterface {
      slug
      livePrimaryOffer {
        id
        price {
          ...MonetaryAmountFragment_monetaryAmount
        }
        ...useStoreAmount_primaryOffer
      }
    }
    ${monetaryAmountFragment}
    ${useStoreAmount.fragments.primaryOffer}
  ` as TypedDocumentNode<MarketCardFooterPrice_anyCardInterface>,
};
